export function getRemRankValue<T>(
  items: T[],
  destinationIndex: number,
  getOrderKey: (item: T) => string,
): string {
  const prev = items.map(getOrderKey)[destinationIndex - 1] ?? '';
  const next = items.map(getOrderKey)[destinationIndex + 1] ?? '';
  if (typeof prev !== 'string' || typeof next !== 'string') {
    throw new Error('orderKey value must be a string');
  }

  return getRemRankSortingString(prev, next);
}

/**
 * A class that provides indexing functionality for a given alphabet.
 *
 * This class creates a bidirectional mapping between characters in the alphabet
 * and their corresponding integer indices. It also provides utility methods
 * for converting between characters and indices, and properties for accessing
 * special characters in the alphabet.
 *
 * The alphabet is expected to be a sorted string of unique characters.
 */
function AlphabetIndexer(alphabet: string) {
  // Verify that the alphabet is sorted and contains unique characters
  const sortedUniqueChars = [...new Set(alphabet)].sort().join('');
  if (alphabet !== sortedUniqueChars) {
    throw new Error(
      'The provided alphabet must be sorted and contain unique characters.',
    );
  }

  // Create bidirectional mappings between characters and their indices
  const charToInt: Record<string, number> = {};
  const intToChar: Record<number, string> = {};

  for (let i = 0; i < alphabet.length; i++) {
    charToInt[alphabet[i]] = i;
    intToChar[i] = alphabet[i];
  }

  return {
    /**
     * Convert a character to its corresponding integer index.
     */
    to_int: (char: string): number => charToInt[char],

    /**
     * Convert an integer index to its corresponding character.
     */
    to_char: (i: number): string => intToChar[i],

    /**
     * The first character of the alphabet.
     */
    get start(): string {
      return alphabet[0];
    },

    /**
     * The last character of the alphabet.
     */
    get end(): string {
      return alphabet[alphabet.length - 1];
    },

    /**
     * The middle character of the alphabet.
     *
     * For even-length alphabets, returns the character to the left of the midpoint.
     * For odd-length alphabets, returns the exact midpoint character.
     */
    get midpoint(): string {
      const midIndex = Math.floor((alphabet.length - 1) / 2);
      return alphabet[midIndex];
    },
  };
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const ALPHA_MAP = AlphabetIndexer(ALPHABET);

const ALPHABET_START = ALPHA_MAP.start;
const ALPHABET_END = ALPHA_MAP.end;
const ALPHABET_MIDPOINT = ALPHA_MAP.midpoint;

// Set our first index value for a new/empty column.
const INIT_ORDINAL = ALPHA_MAP.to_int(ALPHABET_START) + 1;
const INIT_INDEX = ALPHA_MAP.to_char(INIT_ORDINAL);

/**
 * Retrieves an initial index for a new column.
 *
 * @returns The new index.
 */
export function getNewIndex(): string {
  return INIT_INDEX;
}

/**
 * Validates the given index string.
 *
 * @param index The index string to be validated.
 * @throws Error if the index contains any invalid character or ends with ALPHABET_START.
 */
function validateIndex(index: string): void {
  // Index is invalid if any character is not in [ALPHABET_START and ALPHABET_END].
  if ([...index].some((char) => char < ALPHABET_START || char > ALPHABET_END)) {
    throw new Error(
      `Invalid index: '${index}'. ` +
        `Characters must be in range ('${ALPHABET_START}', '${ALPHABET_END}'].`,
    );
  }

  // Index is invalid if it's longer than 1 character and ends with ALPHABET_START.
  if (index.length > 1 && index.endsWith(ALPHABET_START)) {
    throw new Error(
      `Invalid index: '${index}'. Index cannot end with '${ALPHABET_START}'.`,
    );
  }
}

/**
 * Computes the midpoint between two values.
 *
 * @param ordinalA The first ordinal value.
 * @param ordinalB The second ordinal value.
 * @returns The midpoint between the two values.
 */
function computeMidpoint(ordinalA: number, ordinalB: number): number {
  return Math.floor((ordinalA + ordinalB) / 2);
}

/**
 * Computes the intermediate index between two given indices using an expansion flag approach.
 *
 * @param indexBefore The index of the item 'before' the target position.
 * @param indexAfter The index of the item 'after' the target position.
 * @returns The computed intermediate index.
 * @throws Error if the input indices are not valid.
 */
function getRemRankSortingString(
  indexBefore: string,
  indexAfter: string,
): string {
  // For empty strings, return initial index
  if (!indexBefore && !indexAfter) {
    return INIT_INDEX;
  }

  // If either is empty, compute preceding/succeeding index
  if (!indexBefore) {
    return computePrecedingIndex(indexAfter);
  }

  if (!indexAfter) {
    return computeSucceedingIndex(indexBefore);
  }

  // Validate input indices.
  validateIndex(indexBefore);
  validateIndex(indexAfter);

  // Flip order if necessary.
  if (indexBefore > indexAfter) {
    [indexBefore, indexAfter] = [indexAfter, indexBefore];
  }

  let intermediateIndex = '';
  let expandFlag = false;
  const maxLength = Math.max(indexBefore.length, indexAfter.length);

  for (let i = 0; i < maxLength; i++) {
    // Get the ordinal values, using ALPHABET_START as default for padding.
    const lo =
      i < indexBefore.length
        ? ALPHA_MAP.to_int(indexBefore[i])
        : ALPHA_MAP.to_int(ALPHABET_START);

    // For the high value, use ALPHABET_END.
    const hi =
      i < indexAfter.length && !expandFlag
        ? ALPHA_MAP.to_int(indexAfter[i])
        : ALPHA_MAP.to_int(ALPHABET_END);

    if (lo === hi) {
      intermediateIndex += ALPHA_MAP.to_char(lo);
    } else if (hi - lo > 1) {
      intermediateIndex += ALPHA_MAP.to_char(computeMidpoint(lo, hi));
      expandFlag = false;
      return intermediateIndex;
    } else {
      intermediateIndex += ALPHA_MAP.to_char(lo);
      expandFlag = true;
    }
  }

  if (expandFlag) {
    intermediateIndex += ALPHABET_MIDPOINT;
  }

  return intermediateIndex;
}

/**
 * Returns the preceding index value based on the given index.
 *
 * @param index The index value.
 * @returns The preceding index value.
 * @throws Error if the index is not valid.
 */
function computePrecedingIndex(index: string): string {
  validateIndex(index);

  // Iterate backwards through the string.
  for (let i = index.length - 1; i >= 0; i--) {
    const curCharOrdinal = ALPHA_MAP.to_int(index[i]);

    if (curCharOrdinal > INIT_ORDINAL) {
      // Decrement least significant character, e.g. CC -> CB.
      return (
        index.slice(0, i) +
        ALPHA_MAP.to_char(curCharOrdinal - 1) +
        index.slice(i + 1)
      );
    }
  }

  // If we've reached here, all characters were at INIT_ORDINAL already.
  // Expand string and replace previously least significant char,
  // e.g. BB -> BAZ.
  return index.slice(0, -1) + ALPHABET_START + ALPHABET_END;
}

/**
 * Returns the succeeding index based on the given index.
 *
 * @param index The index to find the succeeding index for.
 * @returns The succeeding index.
 * @throws Error if the index is not valid.
 */
function computeSucceedingIndex(index: string): string {
  validateIndex(index);

  if (index.charAt(index.length - 1) === ALPHABET_END) {
    // If the last character is Z, append INIT_INDEX.
    return index + INIT_INDEX;
  }

  // Otherwise, increment the last character.
  return (
    index.slice(0, -1) +
    ALPHA_MAP.to_char(ALPHA_MAP.to_int(index.charAt(index.length - 1)) + 1)
  );
}
